/* eslint-disable react-refresh/only-export-components -- archivo mezcla context y utilidades; split diferido */
import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef, type ReactNode } from "react";
export type { CajaStatus } from "@/modelos/cajas/Caja";
import type { TipoMargenDescuadre } from "@/modelos/Caja";
import type {
  CajaStatus,
  AperturaCaja,
  Movimiento,
  ResumenCaja,
  ResumenMedioPago,
  CierreCaja,
  MedioPago,
  TipoMovimiento,
} from "@/modelos/cajas/Caja";
import { useToast } from "@/contexts/ContextoToast";
import { calcularResumenCaja, calcularResumenEfectivo } from "@/utils/cajas/calculations";
import { DescuadreError, CajaCerradaError, handleCajaError } from "@/utils/cajas/errors";
import { useConfigurationContext } from "@/contexts/ContextoConfiguracion";
import { resolveActiveCajaForEstablecimiento, NoActiveCajaError } from "@/features/configuracion-sistema/utilidades/seleccionCaja";
import { useUserSession } from "@/contexts/UserSessionContext";
import { registrarCajaAbiertaExitoso, registrarCajaCerradaExitoso, registrarMovimientoCajaRegistrado } from "@/shared/analitica/analitica";
import { useCajas } from "@/hooks/useCajas";
import { cajaSesionService } from "@/services/api/cajaSesion.service";
import type { CajaSesionApiDto, MovimientoApiDto, TransaccionApiDto } from "@/services/api/cajaSesion.service";

export interface UltimoCierre {
  fechaHoraCierre: Date;
  usuarioNombre: string;
  montoFinalTotal: number;
  montoFinalPen: number;
  montoFinalUsd: number;
  apertura: number;
  ingresos: number;
  egresos: number;
  saldo: number;
  observaciones?: string;
}

interface CajaContextValue {
  status: CajaStatus;
  sesionId: string | null;
  aperturaActual: AperturaCaja | null;
  ultimoCierre: UltimoCierre | null;
  activeCajaId: string | null;
  activeCajaNombre: string | null;
  activeCajaMediosPago: MedioPago[];
  activeCajaLimiteMaximo: number;
  activeCajaTipoMargen: TipoMargenDescuadre;
  activeCajaMonedaCodigo: string;
  movimientos: Movimiento[];
  historialMovimientos: Movimiento[];
  historialCargado: boolean;
  esSesionDiaAnterior: boolean;

  cajasDisponibles: Array<{ id: string; nombre: string }>;
  setActiveCaja: (cajaId: string) => void;

  margenDescuadre: number;
  setMargenDescuadre: (margen: number) => void;

  abrirCaja: (apertura: Omit<AperturaCaja, 'id' | 'cajaId'>) => Promise<void>;
  cerrarCaja: (cierreCaja: Omit<CierreCaja, 'id' | 'aperturaId'>) => Promise<void>;

  agregarMovimiento: (movimiento: Omit<Movimiento, 'id' | 'fecha' | 'cajaId' | 'aperturaId'>) => Promise<void>;

  registrarTransaccionCaja: (data: {
    origen: string;
    origenId: string;
    origenNumero?: string;
    tipo: string;
    monto: number;
    medioPagoId?: string;
    medioPagoNombre?: string;
    clienteNombre?: string;
    clienteDocumento?: string;
    moneda?: string;
  }) => Promise<void>;

  recargarMovimientos: () => Promise<void>;

  getResumen: () => ResumenCaja;
  getResumenPorMedioPago: () => ResumenMedioPago[];
  getDesgloseMediosPago: () => { ingresos: { nombre: string; monto: number }[]; egresos: { nombre: string; monto: number }[] };

  esCargando: boolean;
}

export const CajaContext = createContext<CajaContextValue | undefined>(undefined);

export const useCaja = () => {
  const context = useContext(CajaContext);
  if (!context) {
    throw new Error("useCaja debe usarse dentro de CajaProvider");
  }
  return context;
};

const mapSesionToApertura = (sesion: CajaSesionApiDto): AperturaCaja => ({
  id: sesion.id,
  cajaId: sesion.cajaId,
  usuarioId: sesion.usuarioAperturaId ?? '',
  usuarioNombre: sesion.usuarioAperturaNombre,
  fechaHoraApertura: new Date(sesion.fechaApertura),
  montoInicialPen: sesion.montoInicialDetalle?.pen ?? sesion.montoInicialDetalle?.efectivo ?? sesion.montoInicial,
  montoInicialUsd: sesion.montoInicialDetalle?.usd ?? 0,
  montoInicialTotal: sesion.montoInicial,
  notas: sesion.notasApertura,
});

const mapApiMovimiento = (mov: MovimientoApiDto, cajaId: string, sesionId: string): Movimiento => ({
  id: mov.id,
  cajaId,
  aperturaId: sesionId,
  tipo: mov.tipo as TipoMovimiento,
  concepto: mov.concepto ?? '',
  medioPago: mov.medioPagoNombre ?? '',
  paymentMeanCode: mov.medioPagoId,
  paymentMeanLabel: mov.medioPagoNombre,
  monto: mov.monto,
  referencia: mov.referencia,
  fecha: new Date(mov.createdAt),
  usuarioId: '',
  usuarioNombre: mov.usuarioNombre ?? '',
  fuente: 'movimiento',
  moneda: 'PEN',
});

const mapApiTransaccion = (tx: TransaccionApiDto, cajaId: string, sesionId: string): Movimiento => ({
  id: tx.id,
  cajaId,
  aperturaId: sesionId,
  tipo: (tx.tipo || 'Ingreso') as TipoMovimiento,
  concepto: `${tx.origen}: ${tx.origenNumero ?? tx.origenId}`,
  medioPago: tx.medioPagoNombre ?? '',
  paymentMeanCode: tx.medioPagoId,
  paymentMeanLabel: tx.medioPagoNombre,
  paymentMeanSunatCode: tx.medioPagoCodigo,
  monto: tx.monto,
  referencia: tx.origenNumero,
  fecha: new Date(tx.createdAt),
  usuarioId: '',
  usuarioNombre: '',
  fuente: 'transaccion',
  moneda: tx.moneda ?? 'PEN',
});

interface CajaProviderProps {
  children: ReactNode;
}

export const CajaProvider = ({ children }: CajaProviderProps) => {
  const [status, setStatus] = useState<CajaStatus>("cerrada");
  const [sesionId, setSesionId] = useState<string | null>(null);
  const [aperturaActual, setAperturaActual] = useState<AperturaCaja | null>(null);
  const [ultimoCierre, setUltimoCierre] = useState<UltimoCierre | null>(null);
  const [activeCajaId, setActiveCajaId] = useState<string | null>(null);
  const [activeCajaNombre, setActiveCajaNombre] = useState<string | null>(null);
  const [activeCajaMediosPago, setActiveCajaMediosPago] = useState<MedioPago[]>([]);
  const [activeCajaLimiteMaximo, setActiveCajaLimiteMaximo] = useState<number>(0);
  const [activeCajaTipoMargen, setActiveCajaTipoMargen] = useState<TipoMargenDescuadre>('porcentaje');
  const [activeCajaMonedaCodigo, setActiveCajaMonedaCodigo] = useState<string>('PEN');
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [historialMovimientos, setHistorialMovimientos] = useState<Movimiento[]>([]);
  const [historialHydrated, setHistorialHydrated] = useState(false);
  const [manualCajaId, setManualCajaId] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem('senciyo_caja_seleccionada');
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      const eid = localStorage.getItem('senciyo_empresa_id');
      const esid = localStorage.getItem('senciyo_establecimiento_id');
      if (parsed.empresaId === eid && parsed.establecimientoId === esid) return parsed.cajaId;
    } catch { /* ignore */ }
    return null;
  });
  const [margenDescuadre, setMargenDescuadre] = useState<number>(1.0);
  const [esCargando, setIsLoading] = useState(false);
  const [cajasHydratadas, setCajasHydratadas] = useState(false);
  const { showSuccess, showError } = useToast();

  const { state: configurationState } = useConfigurationContext();
  const { session } = useUserSession();
  const empresaId = session?.currentCompanyId ?? '';
  const establecimientoId = session?.currentEstablecimientoId ?? '';
  const { cargarCajas } = useCajas();

  const scopeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!empresaId || !establecimientoId) return;
    setCajasHydratadas(false);
    void cargarCajas().finally(() => setCajasHydratadas(true));
  }, [empresaId, establecimientoId, cargarCajas]);

  const aplicarCaja = useCallback((caja: { id: string; nombre: string; mediosPagoPermitidos: MedioPago[]; margenDescuadreCaja: number; limiteMaximoCaja: number; tipoMargenCaja: TipoMargenDescuadre; monedaCodigo?: string | null }) => {
    setActiveCajaId(caja.id);
    setActiveCajaNombre(caja.nombre);
    setActiveCajaMediosPago(caja.mediosPagoPermitidos ?? []);
    setMargenDescuadre(caja.margenDescuadreCaja ?? 1.0);
    setActiveCajaLimiteMaximo(caja.limiteMaximoCaja ?? 0);
    setActiveCajaTipoMargen(caja.tipoMargenCaja ?? 'porcentaje');
    setActiveCajaMonedaCodigo(caja.monedaCodigo ?? 'PEN');
  }, []);

  useEffect(() => {
    if (!empresaId || !establecimientoId) {
      setActiveCajaId(null);
      setActiveCajaNombre(null);
      setActiveCajaMediosPago([]);
      setActiveCajaLimiteMaximo(0);
      setActiveCajaTipoMargen('porcentaje');
      setActiveCajaMonedaCodigo('PEN');
      return;
    }

    const cajas = configurationState.cajas ?? [];

    if (manualCajaId) {
      const caja = cajas.find(c => c.id === manualCajaId && c.establecimientoId === establecimientoId && c.esHabilitada);
      if (caja) {
        aplicarCaja({
          id: caja.id,
          nombre: caja.nombre,
          mediosPagoPermitidos: (caja.medioPagos ?? []).map(mp => ({ code: mp.codigo, label: mp.nombre })),
          margenDescuadreCaja: caja.margenDescuadre ?? 1,
          tipoMargenCaja: ((caja.tipoMargenDescuadre ?? (caja as unknown as { tipoMargen?: string }).tipoMargen) ?? 'porcentaje') as TipoMargenDescuadre,
          limiteMaximoCaja: caja.limiteMaximo ?? 0,
          monedaCodigo: caja.monedaCodigo,
        });
        return;
      }
      if (!cajasHydratadas) return;
      setManualCajaId(null);
    }

    try {
      aplicarCaja(resolveActiveCajaForEstablecimiento({ empresaId, establecimientoId, cajas }));
    } catch (error) {
      if (!(error instanceof NoActiveCajaError)) {
        console.error('[Caja] Error inesperado al resolver caja activa:', error);
      }
      setActiveCajaId(null);
      setActiveCajaNombre(null);
      setActiveCajaMediosPago([]);
      setActiveCajaLimiteMaximo(0);
      setActiveCajaTipoMargen('porcentaje');
      setActiveCajaMonedaCodigo('PEN');
    }
  }, [empresaId, establecimientoId, configurationState.cajas, manualCajaId, cajasHydratadas, aplicarCaja]);

  useEffect(() => {
    const scope = `${empresaId}:${establecimientoId}:${activeCajaId ?? ''}`;
    if (scopeRef.current === scope) return;
    scopeRef.current = scope;

    setStatus('cerrada');
    setAperturaActual(null);
    setSesionId(null);
    setMovimientos([]);
    setHistorialMovimientos([]);
    setHistorialHydrated(false);

    if (!activeCajaId || !establecimientoId) return;

    const aplicarSesion = (sesion: CajaSesionApiDto) => {
      setSesionId(sesion.id);
      setAperturaActual(mapSesionToApertura(sesion));
      setStatus('abierta');
      const movs = sesion.movimientos
        .filter((m) => !['Apertura', 'Cierre'].includes(m.tipo))
        .map((m) => mapApiMovimiento(m, sesion.cajaId, sesion.id));
      const txs = sesion.transacciones.map((tx) => mapApiTransaccion(tx, sesion.cajaId, sesion.id));
      const allMovs = [...movs, ...txs].sort((a, b) => b.fecha.getTime() - a.fecha.getTime());
      setMovimientos(allMovs);
      setHistorialMovimientos(allMovs);
    };

    const loadSesionActiva = async () => {
      try {
        const res = await cajaSesionService.getSesionActiva({ cajaId: activeCajaId });
        if (res.ok && res.data) {
          aplicarSesion(res.data);
        } else if (manualCajaId !== activeCajaId) {
          const otrasHabilitadas = (configurationState.cajas ?? []).filter(
            c => c.establecimientoId === establecimientoId && c.esHabilitada && c.id !== activeCajaId
          );
          for (const otraCaja of otrasHabilitadas) {
            const otherRes = await cajaSesionService.getSesionActiva({ cajaId: otraCaja.id });
            if (otherRes.ok && otherRes.data) {
              scopeRef.current = `${empresaId}:${establecimientoId}:${otraCaja.id}`;
              aplicarCaja({
                id: otraCaja.id,
                nombre: otraCaja.nombre,
                mediosPagoPermitidos: (otraCaja.medioPagos ?? []).map(mp => ({ code: mp.codigo, label: mp.nombre })),
                margenDescuadreCaja: otraCaja.margenDescuadre ?? 1,
                tipoMargenCaja: ((otraCaja.tipoMargenDescuadre ?? (otraCaja as unknown as { tipoMargen?: string }).tipoMargen) ?? 'porcentaje') as TipoMargenDescuadre,
                limiteMaximoCaja: otraCaja.limiteMaximo ?? 0,
                monedaCodigo: otraCaja.monedaCodigo,
              });
              setManualCajaId(otraCaja.id);
              try {
                localStorage.setItem('senciyo_caja_seleccionada', JSON.stringify({
                  empresaId, establecimientoId, cajaId: otraCaja.id,
                }));
              } catch { /* ignore */ }
              aplicarSesion(otherRes.data);
              return;
            }
          }
        }
      } catch {
        // No session found - caja stays closed
      } finally {
        setHistorialHydrated(true);
      }
    };

    void loadSesionActiva();
  }, [empresaId, establecimientoId, activeCajaId, manualCajaId]);

  const abrirCaja = useCallback(async (apertura: Omit<AperturaCaja, 'id' | 'cajaId'>) => {
    if (!activeCajaId || !establecimientoId) {
      const error = new NoActiveCajaError();
      showError(error.message);
      throw error;
    }

    setIsLoading(true);
    try {
      const res = await cajaSesionService.abrir({
        cajaId: activeCajaId,
        establecimientoId,
        montoInicial: apertura.montoInicialTotal,
        montoInicialDetalle: {
          pen: apertura.montoInicialPen,
          usd: apertura.montoInicialUsd,
        },
        notas: apertura.notas,
      });

      if (!res.ok || !res.data) throw new Error((res as any).message ?? res.mensaje ?? 'Error al abrir la caja');

      const sesion = res.data;
      setSesionId(sesion.id);
      setAperturaActual(mapSesionToApertura(sesion));
      setStatus('abierta');
      setMovimientos([]);
      setHistorialMovimientos([]);

      const montosApertura = [
        apertura.montoInicialPen > 0 ? `S/ ${apertura.montoInicialPen.toFixed(2)}` : null,
        apertura.montoInicialUsd > 0 ? `US$ ${apertura.montoInicialUsd.toFixed(2)}` : null,
      ].filter(Boolean);
      showSuccess(`¡Caja abierta! Monto inicial: ${montosApertura.join(' + ') || 'S/ 0.00'}`);
      registrarCajaAbiertaExitoso({
        montoInicialTotal: apertura.montoInicialTotal,
        montoInicialPen: apertura.montoInicialPen,
        montoInicialUsd: apertura.montoInicialUsd,
        cajaId: activeCajaId ?? undefined,
      });
    } catch (error) {
      showError(error instanceof Error ? error.message : 'No se pudo abrir la caja.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [activeCajaId, establecimientoId, showSuccess, showError]);

  const cerrarCaja = useCallback(async (cierreCaja: Omit<CierreCaja, 'id' | 'aperturaId'>) => {
    if (!sesionId || !aperturaActual) {
      const error = new CajaCerradaError();
      showError(error.message);
      return;
    }

    setIsLoading(true);
    try {
      const efPen = calcularResumenEfectivo(movimientos, 'PEN');
      const efUsd = calcularResumenEfectivo(movimientos, 'USD');
      const saldoEfPen = parseFloat(((aperturaActual.montoInicialPen ?? 0) + efPen.ingresos - efPen.egresos).toFixed(2));
      const saldoEfUsd = parseFloat(((aperturaActual.montoInicialUsd ?? 0) + efUsd.ingresos - efUsd.egresos).toFixed(2));

      const descuadrePen = parseFloat((cierreCaja.montoFinalPen - saldoEfPen).toFixed(2));
      const descuadreUsd = parseFloat((cierreCaja.montoFinalUsd - saldoEfUsd).toFixed(2));

      const margenPen = activeCajaTipoMargen === 'monto'
        ? margenDescuadre
        : (margenDescuadre / 100) * Math.abs(saldoEfPen);
      const margenUsd = activeCajaTipoMargen === 'monto'
        ? margenDescuadre
        : (margenDescuadre / 100) * Math.abs(saldoEfUsd);

      if (Math.abs(descuadrePen) > margenPen) {
        throw new DescuadreError(descuadrePen, margenPen);
      }
      if (Math.abs(descuadreUsd) > margenUsd) {
        throw new DescuadreError(descuadreUsd, margenUsd);
      }

      const resumen = calcularResumenCaja(aperturaActual, movimientos);

      const res = await cajaSesionService.cerrar(sesionId, {
        montoCierreDetalle: {
          pen: cierreCaja.montoFinalPen,
          usd: cierreCaja.montoFinalUsd,
        },
        diferenciaPen: descuadrePen,
        diferenciaUsd: descuadreUsd,
        observacion: cierreCaja.observaciones,
      });

      if (!res.ok || !res.data) throw new Error((res as any).message ?? res.mensaje ?? 'Error al cerrar la caja');

      setUltimoCierre({
        fechaHoraCierre: res.data.fechaCierre ? new Date(res.data.fechaCierre) : new Date(),
        usuarioNombre: res.data.usuarioCierreNombre ?? cierreCaja.usuarioNombre,
        montoFinalTotal: cierreCaja.montoFinalTotal,
        montoFinalPen: cierreCaja.montoFinalPen,
        montoFinalUsd: cierreCaja.montoFinalUsd,
        apertura: resumen.apertura,
        ingresos: resumen.ingresos,
        egresos: resumen.egresos,
        saldo: resumen.saldo,
        observaciones: cierreCaja.observaciones,
      });
      setStatus('cerrada');
      setAperturaActual(null);
      setSesionId(null);
      setMovimientos([]);

      const descuadresPorMoneda = [
        Math.abs(descuadrePen) > 0.001 ? `S/ ${descuadrePen.toFixed(2)}` : null,
        Math.abs(descuadreUsd) > 0.001 ? `US$ ${descuadreUsd.toFixed(2)}` : null,
      ].filter(Boolean);
      showSuccess(
        descuadresPorMoneda.length === 0
          ? '¡Caja cerrada! Sin descuadres.'
          : `¡Caja cerrada! Descuadre: ${descuadresPorMoneda.join(' / ')}`
      );
      registrarCajaCerradaExitoso({
        montoFinalTotal: cierreCaja.montoFinalTotal,
        montoFinalPen: cierreCaja.montoFinalPen,
        montoFinalUsd: cierreCaja.montoFinalUsd,
        descuadrePen,
        descuadreUsd,
      });
    } catch (error) {
      const errorMessage = handleCajaError(error);
      showError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [sesionId, aperturaActual, movimientos, margenDescuadre, showSuccess, showError]);

  const agregarMovimiento = useCallback(async (movimiento: Omit<Movimiento, 'id' | 'fecha' | 'cajaId' | 'aperturaId'>) => {
    if (!sesionId || !aperturaActual) {
      const error = new CajaCerradaError();
      showError(error.message);
      return;
    }

    setIsLoading(true);
    try {
      const res = await cajaSesionService.registrarMovimiento(sesionId, {
        tipo: movimiento.tipo,
        concepto: movimiento.concepto,
        monto: movimiento.monto,
        medioPagoId: movimiento.paymentMeanCode,
        medioPagoNombre: movimiento.medioPago || movimiento.paymentMeanLabel,
        referencia: movimiento.referencia,
      });

      if (!res.ok || !res.data) throw new Error((res as any).message ?? res.mensaje ?? 'Error al registrar movimiento');

      const nuevoMov = mapApiMovimiento(res.data, aperturaActual.cajaId, sesionId);
      setMovimientos((prev) => [nuevoMov, ...prev]);
      setHistorialMovimientos((prev) => [nuevoMov, ...prev]);

      showSuccess(`${movimiento.tipo} de S/ ${movimiento.monto.toFixed(2)} registrado.`);
      registrarMovimientoCajaRegistrado({
        tipoMovimiento: movimiento.tipo.toLowerCase() as 'ingreso' | 'egreso' | 'otro',
        monto: movimiento.monto,
        moneda: movimiento.moneda,
        medioPago: movimiento.medioPago || movimiento.paymentMeanLabel,
        concepto: movimiento.concepto,
      });
    } catch (error) {
      const errorMessage = handleCajaError(error);
      showError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [sesionId, aperturaActual, showSuccess, showError]);

  const registrarTransaccionCaja = useCallback(async (data: {
    origen: string;
    origenId: string;
    origenNumero?: string;
    tipo: string;
    monto: number;
    medioPagoId?: string;
    medioPagoCodigo?: string;
    medioPagoNombre?: string;
    clienteNombre?: string;
    clienteDocumento?: string;
    moneda?: string;
  }) => {
    if (!sesionId || status !== 'abierta' || !activeCajaId || !establecimientoId) return;
    try {
      const res = await cajaSesionService.registrarTransaccion({
        cajaId: activeCajaId,
        establecimientoId,
        ...data,
      });
      if (res.ok && res.data) {
        const nuevaMov = mapApiTransaccion(res.data, activeCajaId, sesionId);
        setMovimientos((prev) => [nuevaMov, ...prev]);
        setHistorialMovimientos((prev) => [nuevaMov, ...prev]);
      }
    } catch (error) {
      showError(handleCajaError(error));
    }
  }, [sesionId, status, activeCajaId, establecimientoId, showError]);

  const recargarMovimientos = useCallback(async () => {
    if (!activeCajaId || !sesionId) return;
    try {
      const res = await cajaSesionService.getSesionActiva({ cajaId: activeCajaId });
      if (res.ok && res.data) {
        const sesion = res.data;
        const movs = sesion.movimientos
          .filter((m) => !['Apertura', 'Cierre'].includes(m.tipo))
          .map((m) => mapApiMovimiento(m, sesion.cajaId, sesion.id));
        const txs = sesion.transacciones.map((tx) => mapApiTransaccion(tx, sesion.cajaId, sesion.id));
        setMovimientos([...movs, ...txs].sort((a, b) => b.fecha.getTime() - a.fecha.getTime()));
      }
    } catch {
      // silent fail — movimientos quedan como estaban
    }
  }, [activeCajaId, sesionId]);

  const esSesionDiaAnterior = useMemo((): boolean => {
    if (!aperturaActual) return false;
    const hoy = new Date();
    const a = aperturaActual.fechaHoraApertura;
    return a.getFullYear() !== hoy.getFullYear()
      || a.getMonth() !== hoy.getMonth()
      || a.getDate() !== hoy.getDate();
  }, [aperturaActual]);

  const getResumen = useCallback((): ResumenCaja => {
    return calcularResumenCaja(aperturaActual, movimientos);
  }, [aperturaActual, movimientos]);

  const getResumenPorMedioPago = useCallback((): ResumenMedioPago[] => {
    const grupos = new Map<string, number>();
    let totalOtros = 0;

    for (const mov of movimientos) {
      if (mov.tipo !== 'Ingreso' && mov.tipo !== 'Egreso') continue;
      const signo = mov.tipo === 'Ingreso' ? 1 : -1;
      if (mov.fuente === 'transaccion') {
        const key = mov.medioPago || 'Sin especificar';
        grupos.set(key, (grupos.get(key) ?? 0) + signo * mov.monto);
      } else {
        totalOtros += signo * mov.monto;
      }
    }

    const result: ResumenMedioPago[] = [];
    for (const [medioPago, total] of grupos.entries()) {
      if (total !== 0) result.push({ medioPago, total });
    }
    result.sort((a, b) => b.total - a.total);
    if (totalOtros !== 0) result.push({ medioPago: 'Otros', total: totalOtros });

    return result;
  }, [movimientos]);

  const setActiveCaja = useCallback((cajaId: string) => {
    setManualCajaId(cajaId);
    try {
      localStorage.setItem('senciyo_caja_seleccionada', JSON.stringify({
        empresaId,
        establecimientoId,
        cajaId,
      }));
    } catch { /* ignore */ }
  }, [empresaId, establecimientoId]);

  const cajasDisponibles = useMemo(
    () => (configurationState.cajas ?? []).filter(c => c.establecimientoId === establecimientoId && c.esHabilitada),
    [configurationState.cajas, establecimientoId]
  );

  const getDesgloseMediosPago = useCallback(() => {
    const ingresosMap = new Map<string, number>();
    const egresosMap = new Map<string, number>();

    for (const mov of movimientos) {
      const nombre = mov.medioPago || 'Sin especificar';
      if (mov.tipo === 'Ingreso') {
        ingresosMap.set(nombre, (ingresosMap.get(nombre) ?? 0) + mov.monto);
      } else if (mov.tipo === 'Egreso') {
        egresosMap.set(nombre, (egresosMap.get(nombre) ?? 0) + mov.monto);
      }
    }

    return {
      ingresos: Array.from(ingresosMap.entries()).map(([nombre, monto]) => ({ nombre, monto })),
      egresos: Array.from(egresosMap.entries()).map(([nombre, monto]) => ({ nombre, monto })),
    };
  }, [movimientos]);

  const value = useMemo(
    () => ({
      status,
      sesionId,
      aperturaActual,
      ultimoCierre,
      activeCajaId,
      activeCajaNombre,
      activeCajaMediosPago,
      activeCajaLimiteMaximo,
      activeCajaTipoMargen,
      activeCajaMonedaCodigo,
      movimientos,
      cajasDisponibles,
      setActiveCaja,
      margenDescuadre,
      historialMovimientos,
      historialCargado: historialHydrated,
      esSesionDiaAnterior,
      setMargenDescuadre,
      abrirCaja,
      cerrarCaja,
      agregarMovimiento,
      registrarTransaccionCaja,
      recargarMovimientos,
      getResumen,
      getResumenPorMedioPago,
      getDesgloseMediosPago,
      esCargando,
    }),
    [
      status,
      sesionId,
      aperturaActual,
      ultimoCierre,
      activeCajaId,
      activeCajaNombre,
      activeCajaMediosPago,
      activeCajaLimiteMaximo,
      activeCajaTipoMargen,
      activeCajaMonedaCodigo,
      movimientos,
      cajasDisponibles,
      setActiveCaja,
      margenDescuadre,
      historialMovimientos,
      historialHydrated,
      esSesionDiaAnterior,
      abrirCaja,
      cerrarCaja,
      agregarMovimiento,
      registrarTransaccionCaja,
      recargarMovimientos,
      getResumen,
      getResumenPorMedioPago,
      getDesgloseMediosPago,
      esCargando,
    ]
  );

  return <CajaContext.Provider value={value}>{children}</CajaContext.Provider>;
};
